/**
 * ASSISTED ORDER COMPONENT
 * ========================
 *
 * Page publique /ma-commande pour les clients
 * qui ont reçu un code par SMS de la part d'un agent.
 *
 * Flow:
 * 1. Client entre le code (ex: BEF-A7X9)
 * 2. Système affiche le récapitulatif de la commande
 * 3. Client clique "Payer" → Stripe Checkout
 * 4. Après paiement → Page thank-you standard
 */

import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/services/auth.service';
import { CheckoutService, CreateCheckoutSessionRequest } from '../../core/services/checkout.service';

type PageStep = 'enter_code' | 'loading' | 'show_summary' | 'error' | 'payment_success';

interface AssistedShipmentSummary {
  id: string;
  trackingNumber: string;
  code: string;
  expiresAt: string;
  clientEmail?: string; // Email du client assisté (si fourni par l'agent)
  sender: {
    name: string;
    email?: string;
    phone?: string;
    phonePrefix?: string;
    address?: string;
    /** Numéro de rue, SÉPARÉ de `address` — #1730 ①. */
    houseNumber?: string | null;
    addressComplement?: string | null;
    city: string;
    postalCode?: string;
    country: string;
  };
  receiver: {
    name: string;
    city: string;
    phone: string;
  };
  parcel: {
    weight: number;
    description: string;
    contentType: string;
  };
  service: {
    type: string;
    pickupPoint?: any;
    fragile: boolean;
    emballage: boolean;
  };
  pricing: {
    total: number;
    currency: string;
    breakdown?: any;
  };
  status: string;
}

import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { BEFRET_SUPPORT_PHONE_DISPLAY, BEFRET_SUPPORT_PHONE_TEL } from '../../core/config/contact.config';
@Component({
    selector: 'app-assisted-order',
    templateUrl: './assisted-order.component.html',
    styleUrls: ['./assisted-order.component.css'],
    standalone: true,
    imports: [CommonModule, TranslateModule]
})
export class AssistedOrderComponent implements OnInit {
  /**
   * #1768 — le numéro publié vient de la source unique, jamais du template.
   * `supportPhoneTel` est le MÊME numéro au format `tel:` ; les deux se
   * déduisent l'un de l'autre (cf. `contact.config.spec.ts`).
   */
  readonly supportPhone = BEFRET_SUPPORT_PHONE_DISPLAY;
  readonly supportPhoneTel = BEFRET_SUPPORT_PHONE_TEL;

  @ViewChild('codeInput1') codeInput1!: ElementRef<HTMLInputElement>;

  // État de la page
  currentStep: PageStep = 'enter_code';

  // Code saisi après BEF- : 6 caractères depuis #357 (entropie), les codes
  // legacy à 4 caractères restent acceptés (validité 48h à la transition).
  codeDigits: string[] = ['', '', '', '', '', ''];
  private static readonly CODE_LENGTHS = [4, 6];

  // Données de la commande
  shipment: AssistedShipmentSummary | null = null;

  // États
  isLoading = false;
  isProcessingPayment = false;
  errorMessage = '';
  errorCode = '';
  paymentCancelled = false;

  // Countdown expiration
  expirationCountdown = '';
  private countdownInterval: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private functions: Functions,
    private translate: TranslateService,
    private authService: AuthService,
    private checkout: CheckoutService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['code']) {
        this.prefillCode(params['code']);

        // Si paid=true, afficher la confirmation de paiement
        if (params['paid'] === 'true') {
          this.currentStep = 'payment_success';
          return;
        }

        // Si cancelled=true, charger le récap puis afficher le bandeau d'annulation
        if (params['cancelled'] === 'true') {
          this.paymentCancelled = true;
          this.onSubmitCode();
          return;
        }

        // Sinon, soumettre le code normalement
        this.onSubmitCode();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  /**
   * Pré-remplit le code depuis un paramètre URL
   */
  private prefillCode(code: string): void {
    // Format attendu: BEF-XXXX (legacy) ou BEF-XXXXXX (#357)
    const match = code.toUpperCase().match(/^BEF-?([A-Z0-9]{4,8})$/);
    if (match) {
      const digits = match[1].split('').slice(0, this.codeDigits.length);
      this.codeDigits = this.codeDigits.map((_, i) => digits[i] || '');
    }
  }

  /**
   * Gère la saisie dans les inputs du code
   */
  onCodeInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.toUpperCase();

    // Permettre uniquement les caractères valides
    value = value.replace(/[^A-Z0-9]/g, '');

    const lastIndex = this.codeDigits.length - 1;

    // Si plusieurs caractères collés, distribuer
    if (value.length > 1) {
      const chars = value.split('');
      for (let i = 0; i < chars.length && index + i <= lastIndex; i++) {
        this.codeDigits[index + i] = chars[i];
      }
      // Focus sur le dernier input rempli ou le suivant
      const nextIndex = Math.min(index + chars.length, lastIndex);
      this.focusInput(nextIndex);
    } else {
      this.codeDigits[index] = value.charAt(0) || '';

      // Auto-focus sur le suivant
      if (value && index < lastIndex) {
        this.focusInput(index + 1);
      }
    }

    // Clear error
    this.errorMessage = '';
  }

  /**
   * Gère les touches spéciales (backspace, etc.)
   */
  onCodeKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.codeDigits[index] && index > 0) {
      this.focusInput(index - 1);
    }

    if (event.key === 'Enter' && this.isCodeComplete) {
      this.onSubmitCode();
    }
  }

  /**
   * Focus sur un input spécifique
   */
  private focusInput(index: number): void {
    setTimeout(() => {
      const input = document.getElementById(`code-${index}`) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 10);
  }

  /**
   * Vérifie si le code est complet
   */
  get isCodeComplete(): boolean {
    // Préfixe contigu de 4 (legacy) ou 6 caractères, sans trou
    const firstEmpty = this.codeDigits.findIndex(d => d.length !== 1);
    const length = firstEmpty === -1 ? this.codeDigits.length : firstEmpty;
    const noGaps = this.codeDigits.slice(length).every(d => d === '');
    return noGaps && AssistedOrderComponent.CODE_LENGTHS.includes(length);
  }

  /**
   * Retourne le code complet formaté
   */
  get fullCode(): string {
    return `BEF-${this.codeDigits.join('')}`;
  }

  /**
   * Soumet le code pour récupérer la commande
   */
  async onSubmitCode(): Promise<void> {
    if (!this.isCodeComplete) {
      this.errorMessage = this.translate.instant('ASSISTED_ORDER.ERROR_CODE_INCOMPLETE');
      return;
    }

    this.currentStep = 'loading';
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const getByCode = httpsCallable(this.functions, 'getAssistedShipmentByCode');
      const result: any = await getByCode({ code: this.fullCode });

      if (result.data?.success && result.data?.shipment) {
        this.shipment = result.data.shipment;
        this.currentStep = 'show_summary';
        this.startExpirationCountdown();
      } else {
        throw new Error('Réponse invalide');
      }

    } catch (error: any) {
      console.error('Error fetching assisted shipment:', error);
      this.currentStep = 'error';

      // Parser le message d'erreur Firebase
      const errorCode = error.code || error.message || '';

      if (errorCode.includes('not-found')) {
        this.errorMessage = this.translate.instant('ASSISTED_ORDER.ERROR_CODE_INVALID');
        this.errorCode = 'not-found';
      } else if (errorCode.includes('deadline-exceeded') || errorCode.includes('expired')) {
        this.errorMessage = this.translate.instant('ASSISTED_ORDER.ERROR_CODE_EXPIRED');
        this.errorCode = 'expired';
      } else if (errorCode.includes('already-exists')) {
        this.errorMessage = this.translate.instant('ASSISTED_ORDER.ERROR_ALREADY_PAID');
        this.errorCode = 'already-paid';
      } else if (errorCode.includes('cancelled')) {
        this.errorMessage = this.translate.instant('ASSISTED_ORDER.ERROR_CANCELLED');
        this.errorCode = 'cancelled';
      } else {
        this.errorMessage = this.translate.instant('ASSISTED_ORDER.ERROR_GENERIC');
        this.errorCode = 'generic';
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Démarre le countdown d'expiration
   */
  private startExpirationCountdown(): void {
    if (!this.shipment?.expiresAt) return;

    const updateCountdown = () => {
      const now = new Date();
      const expires = new Date(this.shipment!.expiresAt);
      const diff = expires.getTime() - now.getTime();

      if (diff <= 0) {
        this.expirationCountdown = this.translate.instant('ASSISTED_ORDER.EXPIRED');
        clearInterval(this.countdownInterval);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      this.expirationCountdown = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    updateCountdown();
    this.countdownInterval = setInterval(updateCountdown, 1000);
  }

  /**
   * 🎯 #331-B2 : email du client assisté s'il est connecté sur le site.
   * Token Auth d'abord (comptes email/Google), puis profil Firestore
   * users/{uid} (comptes phone-auth, email peuplé par complete-profile).
   * La page /ma-commande est publique : retourne '' si personne n'est connecté.
   */
  private async resolveClientAccountEmail(): Promise<string> {
    const user = this.authService.user;
    if (!user) return '';
    if (user.email) return user.email;
    try {
      const profile = await this.authService.getUserProfile(user.uid);
      return profile?.email || '';
    } catch {
      return '';
    }
  }

  /**
   * Lance le paiement Stripe
   * Utilise le même endpoint createInvoice que le flow normal
   */
  async onPay(): Promise<void> {
    if (!this.shipment?.id) return;

    this.isProcessingPayment = true;
    this.errorMessage = '';

    try {
      console.log('💳 [Assisted] Creating payment session via createInvoice for', this.shipment.id);

      // Email du client (obligatoire depuis le formulaire agent, jamais l'email de l'agent/sender)
      // 🎯 #331-B2 : si l'agent n'a pas fourni d'email, fallback sur le compte
      // connecté du client (token Auth, puis profil Firestore pour les comptes
      // phone-auth) avant le placeholder.
      const customerEmail = this.shipment.clientEmail
        || await this.resolveClientAccountEmail()
        || 'client@assisted.befret.be';

      console.log('📧 [Assisted] Email utilisé pour Stripe:', {
        clientEmail: this.shipment.clientEmail || 'none',
        senderEmail: this.shipment.sender?.email || 'none',
        finalEmail: customerEmail
      });

      // Construire le payload pour createInvoice (même format que order-summary)
      const stripePayload: CreateCheckoutSessionRequest = {
        email: customerEmail,
        parcel: {
          weight: this.shipment.parcel.weight,
          dimensions: {
            length: 30,
            width: 20,
            height: 15
          },
          // ⚠️ #1686 — prix du transport, pas la valeur des marchandises (champ
          // sans lecteur). La déclaration douanière hors UE est construite
          // serveur-side depuis `shipments.parcelInfo.items` : les articles ne
          // transitent pas par ce payload.
          value: this.shipment.pricing.total
        },
        amount: this.shipment.pricing.total,
        // 🔴 #1730 — CE CHAMP PORTAIT LE DESTINATAIRE, PAS L'EXPÉDITEUR.
        //
        // L'inventaire le classait « inerte » au motif que ce champ de
        // `pendingShipments` n'a aucun lecteur. Mesuré : il en a DEUX, tous
        // deux dans `dpd-orchestration.service.ts`, et le premier est le
        // téléphone de la COLLECTE À DOMICILE :
        //
        //   :1507  collectionAddress.phone = sender.phonePrefix && sender.phone
        //            ? `${prefix}${number}`
        //            : (sender.phone || shipmentData?.phoneNumber)   ← ICI
        //   :1521  fullRecipientData.phone = session.customer_phone
        //            || shipmentData?.phoneNumber || ''
        //
        // Un envoi assisté sans téléphone expéditeur (cas explicitement
        // accepté : « un expéditeur SANS téléphone reste accepté ») donnait
        // donc au chauffeur DPD, pour joindre un client en Belgique, le numéro
        // LOCAL du destinataire à Kinshasa — mesuré : `"810000001"`, sans
        // indicatif, non composable depuis l'Europe.
        //
        // Le tunnel standard envoie ici l'expéditeur en E.164
        // (`order-summary.component.ts:615`, `getNotificationNumber()`). C'est
        // la même valeur qui est envoyée maintenant, et elle est composée à
        // partir de la MÊME source que `sender.phone`/`sender.phonePrefix`
        // ci-dessous : une seule vérité dans le payload.
        phoneNumber: this.senderNotificationNumber(),
        sender: {
          name: this.shipment.sender?.name || 'Client BeFret',
          address: this.shipment.sender?.address || '',
          // #1730 ① — le numéro de rue accompagne la rue jusqu'à DPD, comme le
          // fait déjà le tunnel standard (`order-summary.component.ts:553`).
          // Il était persisté puis perdu à la projection : la collecte à
          // domicile partait à une adresse sans numéro. `undefined` et non
          // `''` — l'orchestration teste la présence pour décider de
          // concaténer, une chaîne vide produirait « Rue X  » avec un espace.
          houseNumber: this.shipment.sender?.houseNumber || undefined,
          addressComplement: this.shipment.sender?.addressComplement || undefined,
          city: this.shipment.sender?.city || '',
          postalCode: this.shipment.sender?.postalCode || '',
          country: this.shipment.sender?.country || 'BE',
          phone: this.shipment.sender?.phone || '',
          // #1730 — plus de `|| '+32'`. Un indicatif belge posé sur un numéro
          // de pays inconnu produit un numéro INEXISTANT, pas une
          // approximation : l'orchestration le concatène tel quel et DPD le
          // compose. Depuis le miroir #1676 (⑤), `createAssistedShipment`
          // refuse un expéditeur dont l'indicatif est inexploitable dès qu'il
          // a un numéro — le repli est mort pour toute commande créée depuis,
          // et le laisser en place le ferait rentrer par la fenêtre pour les
          // documents antérieurs. Vide, la gate expéditeur de l'orchestration
          // rend `dpd_creation_failed` : visible, rejouable, honnête.
          phonePrefix: this.shipment.sender?.phonePrefix || '',
          email: this.shipment.sender?.email || '',
        },
        pType: this.shipment.service?.type || 'shop_pickup',
        language: this.translate.currentLang || 'fr',

        // URLs de redirection custom pour le flow assisté
        // Success → page standard /thank-you qui affiche l'étiquette et le tracking
        successUrl: `${window.location.origin}/thank-you?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/ma-commande?code=${this.shipment.code}&cancelled=true`,

        // Métadonnées pour le webhook - MÊME FORMAT que le flux standard
        metadata: {
          unifiedShipmentId: this.shipment.id,
          trackingNumber: this.shipment.trackingNumber,
          architecture: 'unified_v2',
          flowType: 'assisted',
          source: 'assisted',
          assistedCode: this.shipment.code,

          // logisticsContext - IDENTIQUE au flux standard (order-summary.component.ts)
          logisticsContext: JSON.stringify({
            serviceType: this.shipment.service?.type || 'shop_pickup',
            befretDeliveryMethod: this.shipment.service?.type || 'shop_pickup',
            hasPickupPoint: !!this.shipment.service?.pickupPoint,
            pickupPointId: this.shipment.service?.pickupPoint?.parcelShopId || this.shipment.service?.pickupPoint?.id || null,
            collectionDate: null // Pas de collection date pour assisted
          })
        }
      };

      console.log('📦 [Assisted] Stripe payload:', stripePayload);

      // #1677 — CheckoutService porte l'infrastructure du départ Checkout :
      // POST createInvoice, Bearer token best-effort (#426 — le flux assisté
      // est souvent un lien SMS non authentifié : pas de header, et les promos
      // à limite par user sont alors refusées fail-closed côté serveur), puis
      // redirection (sessionUrl d'abord, repli Stripe.js). Les refus serveur
      // remontent INTACTS et sont traduits dans le catch ci-dessous.
      const response = await this.checkout.createSession(stripePayload);

      console.log('✅ [Assisted] Session created:', response.sessionId);

      await this.checkout.redirectToCheckout(response);

    } catch (error: any) {
      console.error('❌ [Assisted] Error creating payment session:', error);
      // #1182 — le flux assisté n'a AUCUN guard de route : il atteint toujours
      // le portillon serveur. C'est donc le seul endroit où le client peut
      // apprendre que la suspension est volontaire.
      // #1393 — même raison qu'#1182 : le flux assisté n'a aucun guard de
      // route, le portillon serveur est le seul endroit où le client peut
      // apprendre qu'une option de sa commande n'est plus proposée (#1347).
      this.errorMessage = error?.error?.code === 'ORDERING_CLOSED'
        ? (error?.error?.error || this.translate.instant('ORDERING_CLOSED.MESSAGE'))
        : error?.error?.code === 'SHIPPING_OPTION_RETIRED'
          ? this.translate.instant('SHIPPING_OPTION_RETIRED.MESSAGE')
          // #1691 — refus douanier serveur avant encaissement. Le client
          // assisté ne compose PAS son colis (c'est l'agent qui le fait) : le
          // message l'invite à rappeler Befret, il ne lui demande pas une
          // correction qu'aucun écran ne lui offre.
          : error?.error?.code === 'CUSTOMS_ITEMS_REQUIRED'
            ? (error?.error?.tooManyItems !== undefined
              ? this.translate.instant('CUSTOMS_ITEMS_REQUIRED.MESSAGE_TOO_MANY_ASSISTED', {
                  declared: error.error.tooManyItems,
                  max: error.error.maxItems,
                })
              : this.translate.instant('CUSTOMS_ITEMS_REQUIRED.MESSAGE_ASSISTED'))
            // #1904 — total remisé sous 0,50 € : le client assisté ne choisit pas
            // le code promo (l'agent l'a posé), il rappelle Befret.
            : error?.error?.code === 'TOTAL_BELOW_MINIMUM'
              ? this.translate.instant('TOTAL_BELOW_MINIMUM.MESSAGE_ASSISTED')
              // #2156 — refus serveur désormais nommés (plus de troncature
              // silencieuse ; une session déjà payée n'en ouvre pas une autre).
              : error?.error?.code === 'FIELD_TOO_LONG'
                ? this.translate.instant('FIELD_TOO_LONG.MESSAGE_ASSISTED')
                : error?.error?.code === 'SHIPMENT_ALREADY_PAID'
                  ? this.translate.instant('ORDER_ALREADY_PAID.MESSAGE')
                  : error?.error?.code === 'CHECKOUT_IN_PROGRESS'
                    ? this.translate.instant('CHECKOUT_IN_PROGRESS.MESSAGE')
                    : this.translate.instant('ASSISTED_ORDER.ERROR_PAYMENT_INIT');
      this.isProcessingPayment = false;
    }
  }

  /**
   * #1730 — Le numéro de notification/collecte = l'EXPÉDITEUR en E.164.
   *
   * Même composition que `getNotificationNumber()` du tunnel standard
   * (`order-summary.component.ts:760`) : indicatif + numéro nettoyé, et le
   * numéro seul quand l'indicatif manque. Aucune valeur inventée — sans
   * téléphone expéditeur, la chaîne est vide et l'orchestration s'en aperçoit
   * au lieu d'appeler un numéro qui n'est pas celui du client.
   */
  private senderNotificationNumber(): string {
    const prefix = (this.shipment?.sender?.phonePrefix || '').trim();
    const number = (this.shipment?.sender?.phone || '').replace(/[\s\-()]/g, '');
    if (!number) return '';
    return prefix ? `${prefix}${number}` : number;
  }

  /**
   * Réessayer avec un autre code
   */
  onRetry(): void {
    this.codeDigits = ['', '', '', '', '', ''];
    this.shipment = null;
    this.errorMessage = '';
    this.errorCode = '';
    this.currentStep = 'enter_code';

    // Focus sur le premier input
    setTimeout(() => this.focusInput(0), 100);
  }

  /**
   * Réessayer le paiement après annulation
   */
  onRetryPayment(): void {
    this.paymentCancelled = false;
    this.errorMessage = '';
    this.onPay();
  }

  /**
   * Contacter le support
   */
  onContactSupport(): void {
    window.open('https://api.whatsapp.com/send/?phone=32473282139&text&type=phone_number&app_absent=0', '_blank');
  }

  /**
   * Formate le type de service
   */
  getServiceTypeLabel(): string {
    if (!this.shipment) return '';
    const type = this.shipment.service.type;
    if (type === 'home_pickup') {
      return this.translate.instant('ASSISTED_ORDER.SERVICE_HOME_PICKUP');
    }
    return this.translate.instant('ASSISTED_ORDER.SERVICE_SHOP_PICKUP');
  }

  /**
   * Formate le prix
   */
  formatPrice(amount: number, currency: string = 'EUR'): string {
    return new Intl.NumberFormat('fr-BE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }
}
